import {Component, ElementRef, EventEmitter, Input, Output, OnInit, OnDestroy} from '@angular/core';
import {Favorite} from '@models/favorite';
import * as L from 'leaflet';
import {IrisShape} from '@models/city';

// Fix Leaflet default icon paths (broken by webpack)
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'assets/marker-icon-2x.png',
    iconUrl: 'assets/marker-icon.png',
    shadowUrl: 'assets/marker-shadow.png',
});

// Palette RdYlGn (vert = pas cher, rouge = cher)
const COLOR_STOPS = [
    {pos: 0, r: 26, g: 150, b: 65},
    {pos: 0.15, r: 109, g: 184, b: 74},
    {pos: 0.30, r: 166, g: 217, b: 106},
    {pos: 0.45, r: 208, g: 236, b: 106},
    {pos: 0.55, r: 254, g: 224, b: 139},
    {pos: 0.70, r: 253, g: 174, b: 97},
    {pos: 0.85, r: 244, g: 109, b: 67},
    {pos: 1, r: 215, g: 48, b: 39},
];

const TYPE_LABELS: { [key: string]: string } = {
    B: 'Prix moyen au m²',
    A: 'Prix moyen au m² des appartements',
    M: 'Prix moyen au m² des maisons',
    T: 'Prix moyen au m² des terrains',
};

@Component({
    selector: 'app-city-map',
    templateUrl: './city-map.component.html',
    styleUrls: ['./city-map.component.scss']
})
export class CityMapComponent implements OnInit, OnDestroy {

    @Input() favorite: Favorite;
    @Input() preview: boolean;
    @Output() extChange = new EventEmitter();

    selectedType = 'B';

    private map: L.Map;
    private gqLayer: L.LayerGroup;
    private irisLayer: L.LayerGroup;
    private locationLayer: L.LayerGroup;
    private legendControl: L.Control;
    private zoomThreshold: number;
    private gqNames: { [code: string]: string } = {};
    private irisToGq: { [irisCode: string]: string } = {};
    private gqShapes: { [gqCode: string]: IrisShape[] } = {};

    constructor(private el: ElementRef) {
    }

    ngOnInit() {
        if (!this.preview) {
            setTimeout(() => this.initMap(), 500);
        }
        this.extChange.subscribe(() => {
            this.resetMap();
        });
    }

    ngOnDestroy() {
        if (this.map) {
            this.map.remove();
            this.map = null;
        }
    }

    private resetMap() {
        if (this.map) {
            this.map.remove();
            this.map = null;
        }
        this.gqNames = {};
        this.irisToGq = {};
        this.gqShapes = {};
        this.legendControl = null;
        setTimeout(() => this.initMap(), 500);
    }

    onTypeChange() {
        if (!this.map) {
            return;
        }
        this.gqLayer.clearLayers();
        this.irisLayer.clearLayers();
        this.drawZones();
        this.locationLayer.clearLayers();
        this.drawLocation();
    }

    private parseCoord(coord: any): any {
        try {
            const parsed = typeof coord === 'string' ? JSON.parse(coord) : coord;
            const ring = parsed?.coordinates?.[0];
            if (Array.isArray(ring) && ring.length > 2 && Array.isArray(ring[0]) && isFinite(ring[0][0])) {
                return parsed;
            }
            return null;
        } catch {
            return null;
        }
    }

    private initMap() {
        const shapes = this.favorite?.ad_city?.iris_shapes;
        console.log(shapes)
        if (!shapes || shapes.length === 0) {
            return;
        }

        const container = this.el.nativeElement.querySelector('.map-container');
        if (!container) {
            return;
        }

        const center = this.getCenter();
        if (!center || (Array.isArray(center) && (!isFinite(center[0] as number) || !isFinite(center[1] as number)))) {
            return;
        }

        this.buildGqNames();
        this.buildMappings();

        this.map = L.map(container, {
            scrollWheelZoom: true,
            zoomControl: true
        }).setView(center, 13);

        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; OpenStreetMap'
        }).addTo(this.map);

        const singleGq = Object.keys(this.gqShapes).length <= 1;

        this.gqLayer = L.layerGroup();
        this.irisLayer = L.layerGroup();
        this.locationLayer = L.layerGroup().addTo(this.map);

        if (singleGq) {
            this.irisLayer.addTo(this.map);
        } else {
            this.gqLayer.addTo(this.map);
        }

        this.drawZones();
        this.drawLocation();
        this.fitBoundsToShapes();

        // La carte peut être détruite (resetMap, ngOnDestroy) avant ces timers.
        const map = this.map;
        if (!singleGq) {
            setTimeout(() => {
                if (this.map !== map) return;
                this.zoomThreshold = map.getZoom() + 1;
                map.on('zoomend', () => this.updateLayerVisibility());
            }, 100);
        }

        setTimeout(() => {
            if (this.map === map) map.invalidateSize();
        }, 200);
    }

    private getCoords(): { lat: number; lng: number } | null {
        const fav = this.favorite;
        if (fav && fav.latitude && fav.longitude) {
            return {lat: fav.latitude, lng: fav.longitude};
        }
        const ad = fav?.ad;
        if (ad && ad.latitude && ad.longitude) {
            return {lat: ad.latitude, lng: ad.longitude};
        }
        return null;
    }

    private isExactAdresse(): boolean {
        if (this.favorite?.exact_adresse) {
            return true;
        }
        return this.favorite?.ad?.exact_adresse || false;
    }

    private getRadiusLocation(): number {
        return this.favorite?.ad?.radius_location || 1000;
    }

    private getCenterFromShapes(): L.LatLngExpression {
        const shapes = this.favorite?.ad_city?.iris_shapes;
        if (shapes && shapes.length > 0) {
            let totalLat = 0, totalLng = 0, count = 0;
            shapes.forEach(shape => {
                const geojson = this.parseCoord(shape.coord);
                if (!geojson) return;
                geojson.coordinates[0].forEach((c: number[]) => {
                    if (Array.isArray(c) && isFinite(c[0]) && isFinite(c[1])) {
                        totalLng += c[0];
                        totalLat += c[1];
                        count++;
                    }
                });
            });
            if (count > 0) {
                return [totalLat / count, totalLng / count];
            }
        }
        return null;
    }

    private getCenter(): L.LatLngExpression {
        // Priorité aux shapes de ad_city pour centrer sur la bonne ville
        const shapeCenter = this.getCenterFromShapes();
        if (shapeCenter) {
            return shapeCenter;
        }
        const coords = this.getCoords();
        if (coords) {
            return [coords.lat, coords.lng];
        }
        return null;
    }

    private buildGqNames() {
        const mapping = this.favorite.ad_city?.mapping_quartier_iris;
        if (mapping && mapping.quartier) {
            mapping.quartier.forEach(gq => {
                this.gqNames[gq.GQ_c] = gq.GQ_n;
            });
        }
    }

    private buildMappings() {
        const shapes = this.favorite?.ad_city?.iris_shapes;
        const mapping = this.favorite.ad_city?.mapping_quartier_iris;

        if (mapping && mapping.quartier) {
            mapping.quartier.forEach(gq => {
                if (gq.iris) {
                    gq.iris.forEach(iris => {
                        this.irisToGq[iris.IRIS_c] = gq.GQ_c;
                    });
                }
            });
        }

        if (shapes) {
            shapes.forEach(shape => {
                const gqCode = this.irisToGq[shape.id] || 'unknown';
                if (!this.gqShapes[gqCode]) {
                    this.gqShapes[gqCode] = [];
                }
                this.gqShapes[gqCode].push(shape);
            });
        }
    }

    private getPropertyPriceM2(): number {
        const price = this.favorite?.price || this.favorite?.ad?.price;
        const square = this.favorite?.square || this.favorite?.ad?.square;
        if (price && square && square > 0) {
            return Math.round(price / square);
        }
        return 0;
    }

    private getFavoriteGqCode(): string {
        const irisCode = this.favorite?.iris || this.favorite?.ad?.iris;
        if (irisCode && this.irisToGq[irisCode]) {
            return this.irisToGq[irisCode];
        }
        return null;
    }

    private drawLocation() {
        const ad = this.favorite?.ad;
        const coords = this.getCoords();

        if (coords) {
            const latlng: L.LatLngExpression = [coords.lat, coords.lng];
            const adresse = this.favorite.adress || ad?.adress || '';
            const priceM2 = this.getPropertyPriceM2();
            const priceLine = priceM2 > 0 ? `<br>Prix au m² de votre projet : <strong>${priceM2.toLocaleString('fr-FR')} €/m²</strong>` : '';

            if (this.isExactAdresse()) {
                const tooltipContent = adresse
                    ? `<strong>Emplacement du bien : </strong>${adresse}${priceLine}`
                    : `<strong>Emplacement du bien</strong>${priceLine}`;
                L.marker(latlng)
                    .addTo(this.locationLayer)
                    .bindTooltip(tooltipContent, {direction: 'top'});
            } else {
                const radius = this.getRadiusLocation();
                const tooltipContent = adresse
                    ? `<strong>Zone approximative : </strong>${adresse}${priceLine}`
                    : `<strong>Zone approximative du bien</strong>${priceLine}`;
                L.circle(latlng, {
                    radius,
                    color: '#3388ff',
                    fillColor: '#3388ff',
                    fillOpacity: 0.15,
                    weight: 2
                }).addTo(this.locationLayer)
                    .bindTooltip(tooltipContent, {direction: 'top'});
            }
        } else {
            this.highlightFavoriteGq();
        }
    }

    private highlightFavoriteGq() {
        const gqCode = this.getFavoriteGqCode();
        if (!gqCode || !this.gqShapes[gqCode]) {
            return;
        }

        const gqName = this.gqNames[gqCode] || gqCode;
        const priceM2 = this.getPropertyPriceM2();
        const priceLine = priceM2 > 0 ? `<br>Prix au m² de votre projet : <strong>${priceM2.toLocaleString('fr-FR')} €/m²</strong>` : '';

        this.gqShapes[gqCode].forEach(shape => {
            const geojson = this.parseCoord(shape.coord);
            if (!geojson) return;
            const coords = geojson.coordinates[0].map((c: number[]) => [c[1], c[0]] as L.LatLngExpression);
            L.polygon(coords, {
                color: '#1a73e8',
                fillColor: 'transparent',
                fillOpacity: 0,
                weight: 3,
                dashArray: '6, 4'
            }).addTo(this.locationLayer)
                .bindTooltip(`<strong>Quartier du bien : ${gqName}</strong>${priceLine}`, {sticky: true});
        });
    }

    private priceToColor(ratio: number): string {
        const t = Math.max(0, Math.min(1, ratio));
        let lower = COLOR_STOPS[0];
        let upper = COLOR_STOPS[COLOR_STOPS.length - 1];
        for (let i = 0; i < COLOR_STOPS.length - 1; i++) {
            if (t >= COLOR_STOPS[i].pos && t <= COLOR_STOPS[i + 1].pos) {
                lower = COLOR_STOPS[i];
                upper = COLOR_STOPS[i + 1];
                break;
            }
        }
        const range = upper.pos - lower.pos || 1;
        const local = (t - lower.pos) / range;
        const r = Math.round(lower.r + (upper.r - lower.r) * local);
        const g = Math.round(lower.g + (upper.g - lower.g) * local);
        const b = Math.round(lower.b + (upper.b - lower.b) * local);
        return `rgb(${r}, ${g}, ${b})`;
    }

    private getPrice(priceObj: any): number {
        return priceObj?.[this.selectedType]?.pM || 0;
    }

    private formatPrice(val: number): string {
        return val > 0 ? Math.round(val).toLocaleString('fr-FR') + ' €' : '';
    }

    private get typeLabel(): string {
        return TYPE_LABELS[this.selectedType] || TYPE_LABELS['B'];
    }

    private updateLegend(priceMin: number, priceMax: number) {
        if (this.legendControl) {
            this.map.removeControl(this.legendControl);
        }

        const minLabel = this.formatPrice(priceMin);
        const maxLabel = this.formatPrice(priceMax);

        const LegendControl = L.Control.extend({
            onAdd: () => {
                const div = L.DomUtil.create('div', 'iris-legend');
                div.innerHTML = `
                    <span>${minLabel}</span>
                    <div class="iris-legend-gradient"></div>
                    <span>${maxLabel}</span>
                `;
                return div;
            }
        });

        this.legendControl = new LegendControl({position: 'topright'});
        this.legendControl.addTo(this.map);
    }

    private getCommunePrice(): number {
        return this.getPrice(this.favorite.ad_city?.prix_commune);
    }

    private drawZones() {
        const shapes = this.favorite?.ad_city?.iris_shapes;
        if (!shapes || shapes.length === 0) {
            return;
        }

        const pricesGq = this.favorite.ad_city?.prix_commune_gq;
        const pricesIris = this.favorite.ad_city?.prix_commune_iris;
        const label = this.typeLabel;
        const hasGqPrices = pricesGq && Object.keys(pricesGq).length > 0;
        const hasIrisPrices = pricesIris && Object.keys(pricesIris).length > 0;

        const allPrices: number[] = [];
        if (hasGqPrices) {
            Object.keys(this.gqShapes).forEach(gqCode => {
                const p = this.getPrice(pricesGq?.[gqCode]);
                if (p > 0) allPrices.push(p);
            });
        }
        if (hasIrisPrices) {
            shapes.forEach(shape => {
                const p = this.getPrice(pricesIris?.[shape.id]);
                if (p > 0) allPrices.push(p);
            });
        }

        // Fallback : prix commune uniquement quand aucune donnée GQ/IRIS
        const communePrice = this.getCommunePrice();
        const useCommuneFallback = !hasGqPrices && !hasIrisPrices && communePrice > 0;
        if (useCommuneFallback) {
            allPrices.push(communePrice);
        }

        if (allPrices.length === 0) {
            // Dessiner quand même les shapes sans couleur de prix
            this.drawShapesWithoutPrices(shapes, label);
            if (this.legendControl) {
                this.map.removeControl(this.legendControl);
                this.legendControl = null;
            }
            return;
        }

        const priceMin = Math.min(...allPrices);
        const priceMax = Math.max(...allPrices);

        this.updateLegend(priceMin, priceMax);

        // Draw GQ layer
        Object.keys(this.gqShapes).forEach(gqCode => {
            const gqName = this.gqNames[gqCode] || gqCode;
            let priceVal = this.getPrice(pricesGq?.[gqCode]);
            if (!priceVal && useCommuneFallback) priceVal = communePrice;

            let color = '#ccc';
            if (priceVal > 0 && priceMax > priceMin) {
                color = this.priceToColor((priceVal - priceMin) / (priceMax - priceMin));
            } else if (priceVal > 0) {
                color = this.priceToColor(0.5);
            }

            const priceLabel = priceVal > 0
                ? `<br>${label} : <strong>${this.formatPrice(priceVal)}/m²</strong>`
                : '<br><em>Volume de ventes insuffisant<br>pour ce type de bien</em>';

            this.gqShapes[gqCode].forEach(shape => {
                const geojson = this.parseCoord(shape.coord);
                if (!geojson) return;
                const coords = geojson.coordinates[0].map((c: number[]) => [c[1], c[0]] as L.LatLngExpression);
                L.polygon(coords, {
                    color: color,
                    fillColor: color,
                    fillOpacity: 0.55,
                    weight: 0
                }).addTo(this.gqLayer)
                    .bindTooltip(`<strong>${gqName}</strong>${priceLabel}`, {sticky: true});
            });

            // Contour extérieur du GQ
            const edgeCounts: { [key: string]: number } = {};
            const edgeCoords: { [key: string]: [L.LatLngExpression, L.LatLngExpression] } = {};

            this.gqShapes[gqCode].forEach(shape => {
                const geojson = this.parseCoord(shape.coord);
                if (!geojson) return;
                const rawCoords: number[][] = geojson.coordinates[0];
                for (let i = 0; i < rawCoords.length - 1; i++) {
                    const a = rawCoords[i];
                    const b = rawCoords[i + 1];
                    const keyAB = `${a[0]},${a[1]}|${b[0]},${b[1]}`;
                    const keyBA = `${b[0]},${b[1]}|${a[0]},${a[1]}`;

                    if (edgeCounts[keyBA]) {
                        edgeCounts[keyBA]++;
                    } else {
                        edgeCounts[keyAB] = (edgeCounts[keyAB] || 0) + 1;
                        edgeCoords[keyAB] = [[a[1], a[0]], [b[1], b[0]]];
                    }
                }
            });

            Object.keys(edgeCounts).forEach(key => {
                if (edgeCounts[key] === 1) {
                    L.polyline(edgeCoords[key], {
                        color: '#aaa',
                        weight: 1.5
                    }).addTo(this.gqLayer);
                }
            });
        });

        // Draw IRIS layer
        shapes.forEach(shape => {
            const geojson = this.parseCoord(shape.coord);
            if (!geojson) return;
            const coords = geojson.coordinates[0].map((c: number[]) => [c[1], c[0]] as L.LatLngExpression);
            let priceVal = this.getPrice(pricesIris?.[shape.id]);
            if (!priceVal && useCommuneFallback) priceVal = communePrice;

            let color = '#ccc';
            if (priceVal > 0 && priceMax > priceMin) {
                color = this.priceToColor((priceVal - priceMin) / (priceMax - priceMin));
            } else if (priceVal > 0) {
                color = this.priceToColor(0.5);
            }

            const priceLabel = priceVal > 0
                ? `<br>${label} : <strong>${this.formatPrice(priceVal)}/m²</strong>`
                : '<br><em>Volume de ventes insuffisant<br>pour ce type de bien</em>';

            L.polygon(coords, {
                color: '#aaa',
                fillColor: color,
                fillOpacity: 0.55,
                weight: 1.5
            }).addTo(this.irisLayer)
                .bindTooltip(`<strong>${shape.name}</strong>${priceLabel}`, {sticky: true});
        });
    }

    private drawShapesWithoutPrices(shapes: IrisShape[], label: string) {
        shapes.forEach(shape => {
            const geojson = this.parseCoord(shape.coord);
            if (!geojson) return;
            const coords = geojson.coordinates[0].map((c: number[]) => [c[1], c[0]] as L.LatLngExpression);
            L.polygon(coords, {
                color: '#aaa',
                fillColor: '#ccc',
                fillOpacity: 0.3,
                weight: 1.5
            }).addTo(this.gqLayer)
                .bindTooltip(`<strong>${shape.name}</strong><br><em>Volume de ventes insuffisant<br>pour ce type de bien</em>`, {sticky: true});
            L.polygon(coords, {
                color: '#aaa',
                fillColor: '#ccc',
                fillOpacity: 0.3,
                weight: 1.5
            }).addTo(this.irisLayer)
                .bindTooltip(`<strong>${shape.name}</strong><br><em>Volume de ventes insuffisant<br>pour ce type de bien</em>`, {sticky: true});
        });
    }

    private fitBoundsToShapes() {
        const shapes = this.favorite?.ad_city?.iris_shapes;
        if (!shapes || shapes.length === 0) {
            return;
        }

        const allCoords: L.LatLngExpression[] = [];
        shapes.forEach(shape => {
            const geojson = this.parseCoord(shape.coord);
            if (!geojson) return;
            geojson.coordinates[0].forEach((c: number[]) => {
                const lat = c[1];
                const lng = c[0];
                if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180 && (lat !== 0 || lng !== 0)) {
                    allCoords.push([lat, lng]);
                }
            });
        });

        if (allCoords.length > 0) {
            const bounds = L.latLngBounds(allCoords);
            if (bounds.isValid()) {
                this.map.fitBounds(bounds, {padding: [20, 20], maxZoom: 16});
            }
        }
    }

    private updateLayerVisibility() {
        const zoom = this.map.getZoom();
        if (zoom >= this.zoomThreshold) {
            if (!this.map.hasLayer(this.irisLayer)) {
                this.irisLayer.addTo(this.map);
            }
            if (this.map.hasLayer(this.gqLayer)) {
                this.map.removeLayer(this.gqLayer);
            }
        } else {
            if (!this.map.hasLayer(this.gqLayer)) {
                this.gqLayer.addTo(this.map);
            }
            if (this.map.hasLayer(this.irisLayer)) {
                this.map.removeLayer(this.irisLayer);
            }
        }
        this.locationLayer.eachLayer(layer => {
            if ((layer as any).bringToFront) {
                (layer as any).bringToFront();
            }
        });
    }
}
